import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardComponent from "./DashboardComponent";
import { API_URL, UPLOADS_API_URL } from "./config";
import Swal from 'sweetalert2';

function SoftworksAdminAccountPayments() {
    const location = useLocation();
    const navigate = useNavigate();

    const userId = localStorage.getItem('user_id');
    const companyId = localStorage.getItem('company_id');
    const branchId = localStorage.getItem('branch_id');
    const [username, setUsername] = useState(localStorage.getItem('userName'));
    const shiftDetails = JSON.parse(localStorage.getItem('shiftDetails'));
    const dateTimeOpen = shiftDetails ? shiftDetails.date_time_open : "Not available";
    const [currency, setCurrency] = useState('');
    const [currencies, setCurrencies] = useState([]);

    const [companyDetails, setCompanyDetails] = useState({});
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [paymentType, setPaymentType] = useState("Cash");
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState({});
    const [amountGiven, setAmountGiven] = useState(0);
 
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const response = await fetch(`${API_URL}/companysetup/${companyId}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                console.log('Fetched Company Details:', data);
                setCompanyDetails(data);
            } catch (error) {
                console.error('Error fetching company details:', error);
            }
        };

        const fetchCurrencies = async () => {
            try {
                const response = await fetch(`${API_URL}/currency`);
                const data = await response.json();

                // Filter to get unique currencies based on the currency code
                const uniqueCurrencies = Array.from(
                    new Map(data.map(item => [item.code, item])).values()
                );

                setCurrencies(uniqueCurrencies);
            } catch (error) {
                console.error('Error fetching currencies:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        const fetchCustomers = async () => {
            try {
                const response = await fetch(`${API_URL}/companysetup`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                console.log('Fetched Customers:', data);
                setCustomers(data);
            } catch (error) {
                console.error('Error fetching customers:', error);
            }
        };

        const fetchBankAccounts = async () => {
            try {
                const response = await fetch(`${API_URL}/cooperatebanks`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                console.log('Fetched Bank Accounts:', data);
                setBankAccounts(data);
            } catch (error) {
                console.error('Error fetching bank accounts:', error);
            }
        };

        fetchCurrencies();
        fetchCompanyDetails();
        fetchCustomers();
        fetchBankAccounts();
    }, [companyId, branchId]);

    const handleClearTransaction = () => {
        setSelectedCustomer(null);
        setPaymentType("Cash");
        setAmountGiven(0);
        setSelectedBankAccount({});
        setCurrency('');
    };

    const handleCompletePurchase = async () => {
        if (amountGiven <= 0) {
            Swal.fire("Error", "Please enter the amount given.", "error");
            return;
        }

        setLoading(true);
        const paymentMode = paymentType === "Cash" ? "CASH" : `Bank - ${selectedBankAccount.hr_bank_id} - (${selectedBankAccount.bank_name}) - ${selectedBankAccount.acc_number} - ${selectedBankAccount.acc_name}`;
        
        const postBody = {
            propertyid: "",
            company_id: companyId,
            branch_id: branchId,
            project: "",
            daterec: new Date().toISOString().split('T')[0],
            timerec: new Date().toTimeString().split(' ')[0],
            datefor: new Date().toISOString().split('T')[0],
            description: paymentMode,
            quantity: 0,
            combined_receipt_total_quantity: 0,
            amntrec: amountGiven,
            amntrec_credit: 0.00,
            interest: 0.00,
            principal: 0.00,
            deposit: 0.00,
            balance: 0.00,
            receipt_ref_dispatch_locked: "UNLOCKED",
            feesdue: 0.00,
            username: userId,
            confirmed_by: "AUTO",
            authorized_by: "AUTO",
            branch: branchId,
            dispatch_status: "DISPATCHED",
            dispatch_by: "AUTO",
            dispatch_date: new Date().toISOString().split('T')[0],
            dispatch_time: new Date().toTimeString().split(' ')[0],
            dispatched_quantity: 0,
            undispatched_quantity_remaining: 0,
            undispatched_inventory_release_date: "0000-00-00",
            dispatch_sales_shift_id: shiftDetails ? shiftDetails.shift_id : null,
            sales_shifts_id: shiftDetails ? shiftDetails.shift_id : null,
            folio: "CASH",
            pmode: paymentMode,
            currency: currency, // Ensure this is set correctly
            price: 0.00,
            usd_price: 0.00,
            usd_cost_per_unit: 0.00,
            credit_repayment_currency_price: 0.00,
            ref: "00000",
            vat_rate_charged: 0.15,
            vat_invoice_no: "00000",
            discount_requisition_id: "0",
            discount_amount: 0.00,
            discount_cost: 0.00,
            monthsbehind: 0,
            movementafterpayment: 0,
            suspencebal: 0.00,
            suspencedr: 0.00,
            suspencecr: 0.00,
            suspencenarration: "0.00",
            marketing_media_sale_origin: "Online",
            marketing_loyalty_structure_id: "00000",
            customerid: selectedCustomer ? selectedCustomer.company_id : null,
            comment: "00000",
            sync_status: "00000"
        };

        console.log("Posting Sale Transaction:", postBody);

        try {
            const response = await fetch(`${API_URL}/salepostrans/add_sale_pos_trans_sold`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(postBody),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const result = await response.json();
            const currentTrans = result.result.insertId; 

            const receiptDetails = {
                paymentId: currentTrans,
                totalAmount: amountGiven,
                operatorName: userId,
                branchName: branchId,
                shiftId: shiftDetails ? shiftDetails.shift_id : null,
                customer: selectedCustomer,
                paymentType: paymentType,
                bankDetails: paymentType === "Bank" ? selectedBankAccount : null,
                companyDetails,
            };

            const saleRecord = {
                company_id: companyId,
                branch_id: branchId,
                sale_records_id: currentTrans,
                product_id: 23, 
                quantity: 1, 
                unit_cost: 100.00, 
                selling_price: 120.00, 
            };

            console.log("Posting Sale Record:", saleRecord);

            try {
                const saleResponse = await fetch(`${API_URL}/salelist/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(saleRecord),
                });
                if (!saleResponse.ok) {
                    throw new Error("Failed to post sale record");
                }

               // Navigate only after sale record is successfully posted
                navigate("/ReceiptPayments", {
                    state: receiptDetails,
                });

            } catch (error) {
                console.error("Error posting sale record:", error);
            }

        } catch (error) {
            console.error("Error completing purchase:", error);
        } finally {
            handleClearTransaction(); 
            setLoading(false);
        }
    };

    return (
        <DashboardComponent>
            <div className="container-fluid" style={{
                backgroundColor: "#f8f9fa",
                minHeight: "100vh",
                padding: "20px",
                marginTop: "30px",
                width: "100%",
            }}>
                <div className="row">
                    {/* Company Details Card */}
                    <div className="col-md-6">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-body text-center">
                                <img
                                    src={`${UPLOADS_API_URL}/${companyDetails.horizontal_logo?.replace(/\\/g, "/")}`}
                                    alt={`${companyDetails.name} Logo`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        maxHeight: '150px',
                                        objectFit: 'contain',
                                        marginBottom: '10px'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Branch Details Card */}
                    <div className="col-md-6">
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                            <div className="card-body text-start">
                                <h5 className="card-title">{companyDetails.name}</h5>
                                <p className="card-text"><strong>Registration Number:</strong> {companyDetails.registration_number}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <header className="my-3 text-center">
                    <h1 className="my-3" style={{ color: "green", fontSize: "1.8rem", fontWeight: 'bold' }}>Account Payments</h1>
                </header>

                {loading && (
                    <div className="loading-overlay">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p>Processing your transaction...</p>
                    </div>
                )}

                <div className="row">
                    <div className="col-md-8 col-sm-12">
                        <div className="mb-3">
                            <select
                                className="form-control mb-2"
                                onChange={(e) => {
                                    const selectedCustomerId = e.target.value;
                                    const customer = customers.find(c => c.company_setup_id.toString() === selectedCustomerId);
                                    setSelectedCustomer(customer);
                                    console.log("Selected Customer Details:", customer);
                                }}
                            >
                                <option value="">Select Customer</option>
                                {customers.map(customer => (
                                    <option key={customer.company_setup_id} value={customer.company_setup_id}>
                                        {customer.name}
                                    </option>
                                ))}
                            </select>

                            <label htmlFor="amountGiven">Amount Given</label>
                            <input
                                type="number"
                                className="form-control mb-2"
                                id="amountGiven"
                                placeholder="Enter amount"
                                value={amountGiven}
                                onChange={(e) => setAmountGiven(Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="col-md-4 col-sm-12">
                        <div className="row mb-3">
                            <div className="col text-start">
                                <label style={{ fontSize: "1rem" }}>Payment Type</label>
                                <select
                                    className="form-control"
                                    value={paymentType}
                                    onChange={(e) => setPaymentType(e.target.value)}
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Bank">Bank</option>
                                </select>
                            </div>
                        </div>

                        {/* <div className="row mb-3">
                            <div className="col text-start">
                                <label style={{ fontSize: "1rem" }}>Currency</label>
                                <select
                                    className="form-control"
                                    value={currency}
                                    onChange={(e) => {
                                      setCurrency(e.target.value);
                                      console.log('Selected Currency:', e.target.value);
                                  }}
                                >
                                    {currencies.map((curr, index) => (
                                        <option key={index} value={curr.code}>
                                            {curr.code} {/* Display the currency code */}
                                        {/* </option>
                                    ))}
                                </select>
                            </div>
                        </div> */} 


                        <div className="row mb-3">
                            <div className="col text-start">
                                <label style={{ fontSize: "1rem" }}>Currency</label>

                               <select
                                    className="form-control"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                >
                                    <option value="USD">USD</option>
                                    <option value="ZWL">ZWL</option>
                                    <option value="ZAR">ZAR</option>
                                    <option value="GBP">GBP</option>

                                </select>
                            </div>
                        </div>

                        
                        {paymentType === "Bank" && (
                            <div className="row mb- text-start">
                                <div className="col text-start">
                                    <label style={{ fontSize: "1rem" }}>Select Bank Account</label>
                                    <select
                                        className="form-control"
                                        value={selectedBankAccount.hr_bank_id || ""}
                                        onChange={(e) => {
                                            const bankId = e.target.value;
                                            const bankAccount = bankAccounts.find(account => account.hr_bank_id.toString() === bankId);
                                            setSelectedBankAccount(bankAccount || {});
                                        }}
                                    >
                                        <option value="">Select Bank Account</option>
                                        {bankAccounts.map(account => (
                                            <option key={account.hr_bank_id} value={account.hr_bank_id}>
                                                {account.bank_name} - {account.acc_name} ({account.acc_number})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-center mt-3">
                            <button 
                                className="btn btn-success me-2" 
                                onClick={handleCompletePurchase}
                            >
                                Complete Payment
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleClearTransaction}
                            >
                                Clear Transaction
                            </button>
                        </div>
                    </div>
                </div>

                <footer className="mt-4 text-center" style={{ fontSize: "0.9rem" }}>
                    <div className="row">
                        <div className="col">
                            <p style={{ color: "#333" }}>Company Name:<span style={{fontWeight: 'bold'}}>{companyDetails.name}</span> </p>
                        </div>
                        <div className="col">
                            <p style={{ color: "#333" }}>Shift ID: <span style={{fontWeight: 'bold'}}>{shiftDetails ? shiftDetails.shift_id : "No active shift"}</span> </p>
                        </div>
                        <div className="col">
                            <p style={{ color: "#333" }}>Shift Open Time: <span style={{fontWeight: 'bold'}}>{dateTimeOpen}</span></p>
                        </div>
                        <div className="col">
                            <p style={{ color: "#333" }}>Till Operator: <span style={{fontWeight: 'bold'}}>{username}</span></p>
                        </div>
                    </div>
                    <hr />
                </footer>
            </div>
        </DashboardComponent>
    );
}

export default SoftworksAdminAccountPayments;